import React from 'react';
import { Link } from 'react-router-dom';
import { Artist } from './types';
import DataTable, { DataTableColumn } from '../ui/DataTable';
import RowActionsMenu, { RowMenuItem, RowMenuLink } from '../ui/RowActionsMenu';
import ScoreRing from '../ui/ScoreRing';

interface RosterTableProps {
  artists: Artist[];
  onDelete: (id: string) => void;
}

const RosterTable: React.FC<RosterTableProps> = ({ artists, onDelete }) => {
  const profileIncomplete = (artistId: string) => {
    const artist = artists.find(a => a.id === artistId);
    if (!artist) return false;

    // Check if any required fields are missing
    return !artist.artistName ||
           !artist.spotifyURI ||
           !artist.appleID ||
           !artist.asin ||
           !artist.soundcloudURL ||
           !artist.tidalURL;
  };
  // console.log('Rendering RosterTable with artists:', artists);

  if (artists.length === 0) {
    return (
      <div className="py-12 text-center">
        <svg
          className="w-12 h-12 mx-auto text-neutral-400"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3"
          />
        </svg>
        <h3 className="mt-2 text-sm font-medium text-black">No artists</h3>
        <p className="mt-1 text-sm text-neutral-500">Get started by adding your first artist.</p>
        <div className="mt-6">
          <Link
            to="/roster/add-artist"
            className="inline-flex items-center px-4 py-2 text-sm font-medium text-white border border-transparent rounded-lg shadow-sm bg-primary-700 hover:bg-primary-800"
          >
            Add Artist
          </Link>
        </div>
      </div>
    );
  }

  const columns: DataTableColumn<Artist>[] = [
    {
      key: "photo",
      header: "Photo",
      // Placeholder kept as-is per the brief: person glyph, not initials.
      cell: (artist) => (
        <div className="w-10 h-10 overflow-hidden rounded-full bg-neutral-100">
          {artist.photo ? (
            <img
              src={artist.photo}
              alt={artist.artistName}
              className="object-cover w-full h-full"
            />
          ) : (
            <div className="flex items-center justify-center w-full h-full">
              <svg className="w-6 h-6 text-neutral-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
            </div>
          )}
        </div>
      ),
    },
    {
      key: "artistName",
      header: <span data-tour="artist-name-column">Artist Name</span>,
      cell: (artist, idx) => (
        <div className="flex items-center gap-2">
          <Link
            to={`/roster/artist-details/${artist.id}?tab=details`}
            className="text-sm font-medium text-black cursor-pointer hover:text-primary-700 hover:underline"
            {...(idx === 0 ? { "data-tour": "artist-name-link" } : {})}
          >
            {artist.artistName}
          </Link>
          {profileIncomplete(artist.id) && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium border rounded-full text-warning-700 bg-warning-50 border-warning-200">
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              Incomplete Profile
            </span>
          )}
        </div>
      ),
    },
    {
      key: "popularity",
      header: "Popularity",
      // Same treatment as Past Submissions: a real 0-100 score reads as a
      // compact ring, so the column costs one small square rather than a bare
      // number. `?? "No Data"` also swallowed a legitimate 0 into the fallback.
      cell: (artist) => {
        const score = artist.popularity?.spotify;
        return score !== undefined && score !== null ? (
          <ScoreRing value={score} label="Popularity" />
        ) : (
          <span className="text-neutral-400">Not Available</span>
        );
      },
    },
    { key: "pitchCount", header: "Pitches Submitted", cell: (artist) => artist.pitchCount },
    {
      key: "actions",
      header: "Actions",
      cell: (artist) => (
        <RowActionsMenu label={`Actions for ${artist.artistName || "artist"}`}>
          <RowMenuLink to={`/roster/artist-details/${artist.id}?tab=pitches`}>
            View Pitches
          </RowMenuLink>
          <RowMenuLink to={`/roster/artist-details/${artist.id}?tab=assets`}>
            View Assets
          </RowMenuLink>
          <RowMenuItem tone="danger" onClick={() => onDelete(artist.id)}>
            Delete Artist
          </RowMenuItem>
        </RowActionsMenu>
      ),
    },
  ];

  return (
    <DataTable
      // rounded-t-none: the search toolbar above supplies the top corners, so
      // the two read as one card rather than two stacked ones.
      className="rounded-t-none"
      columns={columns}
      rows={artists}
      getRowKey={(artist) => artist.id}
    />
  );
};

export default RosterTable;